(function () {
  'use strict';

  /* Drift rails — a marquee the customer can also scroll themselves.
   *
   * The old build animated `transform: translateX()` on a track inside an
   * `overflow: hidden` box, so the row moved on its own and a thumb could do
   * nothing but wait for the card it wanted to come round again.
   *
   * Here the box is a real scroll container and the drift is `scrollLeft`
   * advanced on requestAnimationFrame, at the same px/sec the keyframe used.
   * A touch, a wheel or a trackpad swipe takes it over instantly with native
   * momentum, and the drift picks up again from wherever they left it.
   *
   * Markup contract:
   *   [data-drift-rail]                 the scroll container
   *     [data-drift-track]              the flex row
   *       [data-drift-set] x3           three identical copies of the content
   *   data-drift-duration="42"          seconds to travel one copy
   *
   * Three copies, not two: the rail rests in the MIDDLE one, so there is a
   * whole copy of runway in either direction before a flick can reach an edge.
   * The teleport back to the middle happens only once the scroll has come to
   * rest, because rewriting scrollLeft mid-momentum kills momentum on iOS.
   */

  var RESUME_MS = 2200; // quiet time after the customer's last input
  var SETTLE_MS = 140;  // no scroll event for this long counts as "at rest"
  var OURS_PX = 2;      // scrollLeft within this of our own value is our drift

  function columnGap(el) {
    var g = parseFloat(window.getComputedStyle(el).columnGap);
    return isNaN(g) ? 0 : g;
  }

  function wire(rail) {
    if (rail.dataset.driftWired === 'true') return;

    var track = rail.querySelector('[data-drift-track]');
    if (!track) return;
    var sets = track.querySelectorAll('[data-drift-set]');
    if (sets.length < 3) return;
    var MAX_COPIES = 8;

    rail.dataset.driftWired = 'true';

    var perSet = parseFloat(rail.dataset.driftDuration) || 40;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    var setWidth = 0;
    var pos = 0;
    var hovered = false;
    var focused = false;
    var onScreen = true;
    var holdUntil = 0;
    var drifting = false;
    var lastTs = 0;
    var raf = null;
    var settleTimer = null;
    var resizeTimer = null;

    function measure() {
      var w = sets[0].getBoundingClientRect().width + columnGap(track);
      if (w > 0) setWidth = w;
    }

    function maxScroll() {
      return Math.max(0, rail.scrollWidth - rail.clientWidth);
    }

    /* The loop needs a copy of runway behind the resting point and a viewport
       plus a copy ahead of it, so the row has to be at least
       `viewport / copy + 2` copies long. Liquid ships three, which covers a
       phone and an ordinary laptop. On a wide monitor one copy is narrower
       than the viewport and three is not enough, and the rail would pin
       itself to its own end instead of looping. Top it up here. */
    function ensureCopies() {
      measure();
      if (!setWidth) return;
      var needed = Math.min(MAX_COPIES, Math.ceil(rail.clientWidth / setWidth) + 2);
      for (var i = sets.length; i < needed; i++) {
        var copy = sets[0].cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        /* A clone carrying the editor's block id makes Customize select a copy
           the merchant cannot see. */
        if (copy.hasAttribute('data-shopify-editor-block')) copy.removeAttribute('data-shopify-editor-block');
        Array.prototype.forEach.call(copy.querySelectorAll('[data-shopify-editor-block]'), function (n) {
          n.removeAttribute('data-shopify-editor-block');
        });
        track.appendChild(copy);
      }
      sets = track.querySelectorAll('[data-drift-set]');
    }

    function write(x) {
      pos = x;
      rail.scrollLeft = x;
    }

    /* Bring the rail back into the second copy. Only safe at rest.
       The half-pixel slack matters: the browser stores scrollLeft rounded to
       the device pixel, so reading back what we just wrote can land a hair
       UNDER setWidth, and a bare `<` sent the rail a whole copy forward. */
    function normalise() {
      if (!setWidth) return;
      var max = maxScroll();
      var sl = rail.scrollLeft;
      var next = sl;
      while (next >= setWidth * 2 - 0.5) next -= setWidth;
      while (next < setWidth - 0.5) next += setWidth;
      while (next > max && next - setWidth >= 0) next -= setWidth;
      if (next > max) next = max;
      if (Math.abs(next - sl) > 0.5) write(next);
      else pos = sl;
    }

    function park() {
      ensureCopies();
      if (!setWidth) return;
      write(Math.min(setWidth, maxScroll()));
    }

    function hold() {
      holdUntil = Date.now() + RESUME_MS;
      drifting = false;
    }

    function canDrift() {
      return !hovered && !focused && onScreen && !document.hidden &&
             !reduce.matches && Date.now() >= holdUntil;
    }

    function frame(ts) {
      raf = window.requestAnimationFrame(frame);

      if (!canDrift()) { drifting = false; return; }
      if (!setWidth) measure();
      if (!setWidth) return;

      /* First frame back: adopt wherever the customer left the rail, so the
         drift never yanks it to a remembered position. */
      if (!drifting) { drifting = true; pos = rail.scrollLeft; lastTs = ts; return; }

      var dt = ts - lastTs;
      lastTs = ts;
      if (dt <= 0) return;
      /* A gap this long means the tab was away or the thread was blocked.
         Skip it rather than lurching forward by whatever piled up. */
      if (dt > 400) return;
      /* Anything merely slow should make the drift slower, not stop it.
         Returning early on every long frame froze the rail outright on a
         throttled renderer. */
      if (dt > 50) dt = 50;

      var next = pos + (setWidth / perSet) * (dt / 1000);
      if (next >= setWidth * 2 - 0.5) next -= setWidth;
      if (next > maxScroll()) { normalise(); return; }
      write(next);
    }

    function start() {
      if (raf === null) { drifting = false; raf = window.requestAnimationFrame(frame); }
    }
    function stop() {
      if (raf !== null) { window.cancelAnimationFrame(raf); raf = null; drifting = false; }
    }

    rail.addEventListener('scroll', function () {
      /* Our own drift writes land within a pixel of what we asked for.
         Anything further is the customer, and the drift stands down. */
      if (Math.abs(rail.scrollLeft - pos) < OURS_PX) return;
      hold();
      if (settleTimer) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(normalise, SETTLE_MS);
    }, { passive: true });

    ['pointerdown', 'touchstart', 'wheel'].forEach(function (evt) {
      rail.addEventListener(evt, hold, { passive: true });
    });

    rail.addEventListener('mouseenter', function () { hovered = true; });
    rail.addEventListener('mouseleave', function () { hovered = false; });
    rail.addEventListener('focusin', function () { focused = true; });
    rail.addEventListener('focusout', function () { focused = false; });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    window.addEventListener('resize', function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () { ensureCopies(); normalise(); }, 150);
    }, { passive: true });

    window.addEventListener('load', function () { ensureCopies(); normalise(); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        if (onScreen) start(); else stop();
      }, { rootMargin: '200px 0px' }).observe(rail);
    }

    park();
    start();
  }

  function scan() {
    var rails = document.querySelectorAll('[data-drift-rail]');
    Array.prototype.forEach.call(rails, wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', scan);
})();
